import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useUnitats } from '../../hooks/useUnitats';
import useFetch from '../../hooks/useFetch';

import { axiosInstance } from '../../config/axios';

// ─── Styled components for the dialog ──────────────────────────────────────

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
`;

const Dialog = styled.div`
	background: white;
	border-radius: 12px;
	max-width: 520px;
	width: 100%;
	max-height: 90vh;
	overflow-y: auto;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 24px 24px 16px;
	border-bottom: 1px solid #e9ecef;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 1.4em;
	color: #2c3e50;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 1.5em;
	cursor: pointer;
	color: #666;
	padding: 0;
	line-height: 1;

	&:hover {
		color: #333;
	}
`;

const Body = styled.div`
	padding: 24px;
`;

const FieldGroup = styled.div`
	margin-bottom: 20px;
`;

const Label = styled.label`
	display: block;
	font-weight: 600;
	font-size: 0.9em;
	color: #2c3e50;
	margin-bottom: 6px;
`;

const Select = styled.select`
	width: 100%;
	padding: 10px 14px;
	border: 1px solid #ddd;
	border-radius: 6px;
	font-size: 1em;
	background: white;
	cursor: pointer;
	transition: border-color 0.2s ease;

	&:hover {
		border-color: #3498db;
	}

	&:focus {
		outline: none;
		border-color: #3498db;
		box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
	}
`;

const SliderContainer = styled.div`
	margin-bottom: 20px;
`;

const SliderHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
`;

const SliderValue = styled.span`
	font-weight: 700;
	font-size: 1.1em;
	color: #2c3e50;
`;

const Slider = styled.input`
	width: 100%;
	height: 6px;
	-webkit-appearance: none;
	appearance: none;
	background: #e0e0e0;
	border-radius: 3px;
	outline: none;
	cursor: pointer;

	&::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 22px;
		height: 22px;
		border-radius: 50%;
		background: #3498db;
		cursor: pointer;
		transition: background 0.2s ease;
	}

	&::-moz-range-thumb {
		width: 22px;
		height: 22px;
		border-radius: 50%;
		background: #3498db;
		cursor: pointer;
		border: none;
	}

	&:hover::-webkit-slider-thumb {
		background: #2980b9;
	}
`;

const SliderLabels = styled.div`
	display: flex;
	justify-content: space-between;
	font-size: 0.8em;
	color: #888;
	margin-top: 4px;
`;

const Input = styled.input`
	width: 100%;
	padding: 10px 14px;
	border: 1px solid #ddd;
	border-radius: 6px;
	font-size: 1em;
	transition: border-color 0.2s ease;
	box-sizing: border-box;

	&:focus {
		outline: none;
		border-color: #3498db;
		box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
	}
`;

const Actions = styled.div`
	display: flex;
	gap: 12px;
	padding: 0 24px 24px;
`;

const SubmitButton = styled.button<{ $submitted?: boolean }>`
	flex: 1;
	padding: 12px 20px;
	border-radius: 6px;
	font-size: 1em;
	cursor: pointer;
	border: none;
	font-weight: 600;
	transition: all 0.2s ease;
	background: ${({ $submitted }) => ($submitted ? '#27ae60' : '#3498db')};
	color: white;

	&:hover {
		background: ${({ $submitted }) => ($submitted ? '#219a52' : '#2980b9')};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const CancelButton = styled.button`
	padding: 12px 20px;
	border-radius: 6px;
	font-size: 1em;
	cursor: pointer;
	border: 1px solid #ddd;
	background: white;
	color: #666;
	font-weight: 500;
	transition: all 0.2s ease;

	&:hover {
		background: #f8f9fa;
		border-color: #ccc;
	}
`;

const SubmittedBadge = styled.div`
	background: #d4edda;
	border: 1px solid #c3e6cb;
	border-radius: 6px;
	padding: 10px 14px;
	margin-bottom: 20px;
	color: #155724;
	font-size: 0.9em;
	text-align: center;
`;

const ErrorMessage = styled.div`
	background: #f8d7da;
	border: 1px solid #f5c6cb;
	border-radius: 6px;
	padding: 10px 14px;
	margin-bottom: 16px;
	color: #721c24;
	font-size: 0.9em;
	text-align: center;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────

const generateSchoolYears = (): string[] => {
	const years: string[] = [];
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth();
	const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;

	for (let i = 0; i < 15; i++) {
		const year = startYear - i;
		years.push(`${year}-${year + 1}`);
	}
	years.push('Varis / Desconegut');
	return years;
};

// Generate or retrieve a persistent fingerprint from localStorage
const getFingerprint = (): string => {
	const key = 'aeig_classifier_fingerprint';
	let fp = localStorage.getItem(key);
	if (!fp) {
		fp = crypto.randomUUID
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		localStorage.setItem(key, fp);
	}
	return fp;
};

// ─── Props ─────────────────────────────────────────────────────────────────

interface ClassificarDialogProps {
	albumId: string;
	albumName: string;
	onClose: () => void;
	onClassified?: () => void;
}

// ─── Component ─────────────────────────────────────────────────────────────

const ClassificarDialog: React.FC<ClassificarDialogProps> = ({
	albumId,
	albumName,
	onClose,
	onClassified,
}) => {
	const queryClient = useQueryClient();
	const { unitats } = useUnitats();
	const schoolYears = useMemo(() => generateSchoolYears(), []);
	const fingerprint = useMemo(() => getFingerprint(), []);

	const [unitat, setUnitat] = useState('');
	const [schoolYear, setSchoolYear] = useState(schoolYears[0] || '');
	const [confidence, setConfidence] = useState(50);
	const [respondentLabel, setRespondentLabel] = useState('');
	const [submitted, setSubmitted] = useState<{
		unitat: string;
		schoolYear: string;
		confidence: number;
	} | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Fetch existing classification for this fingerprint on mount
	const { data: myClassificationData } = useFetch<{
		classification: {
			unitat: string;
			schoolYear: string;
			confidence: number;
			respondentLabel?: string;
		} | null;
	}>({
		queryKey: ['my-classification', albumId, fingerprint],
		url: `/api/galeria/albums/${albumId}/my-classification?fingerprint=${encodeURIComponent(fingerprint)}`,
	});

	useEffect(() => {
		if (myClassificationData?.classification) {
			const c = myClassificationData.classification;
			setUnitat(c.unitat);
			setSchoolYear(c.schoolYear);
			setConfidence(c.confidence);
			if (c.respondentLabel) setRespondentLabel(c.respondentLabel);
			setSubmitted({
				unitat: c.unitat,
				schoolYear: c.schoolYear,
				confidence: c.confidence,
			});
		}
	}, [myClassificationData]);

	const handleSubmit = useCallback(async () => {
		if (!unitat) {
			setError('Selecciona una unitat');
			return;
		}
		if (!schoolYear) {
			setError('Selecciona un curs');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const payload: Record<string, unknown> = {
				unitat,
				schoolYear,
				confidence,
				fingerprint,
			};
			if (respondentLabel.trim()) {
				payload.respondentLabel = respondentLabel.trim();
			}

			await axiosInstance.post(
				`/api/galeria/albums/${albumId}/classify`,
				payload,
			);

			// Invalidate the my-classification query so the form loads fresh data next time
			queryClient.invalidateQueries({
				queryKey: ['my-classification', albumId, fingerprint],
			});

			setSubmitted({ unitat, schoolYear, confidence });
			onClassified?.();
		} catch (err: any) {
			setError(
				err?.response?.data?.error || 'Error en enviar la classificació',
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [unitat, schoolYear, confidence, fingerprint, respondentLabel, albumId, onClassified]);

	return (
		<Overlay onClick={onClose}>
			<Dialog onClick={(e) => e.stopPropagation()}>
				<Header>
					<Title>Classifica l'àlbum</Title>
					<CloseButton onClick={onClose}>&times;</CloseButton>
				</Header>
				<Body>
					<p style={{ margin: '0 0 16px', color: '#555', fontSize: '0.95em' }}>
						<strong>{albumName}</strong>
					</p>

					{submitted && (
						<SubmittedBadge>
							✔ Classificació enviada! Pots modificar-la si cal.
						</SubmittedBadge>
					)}

					{error && <ErrorMessage>{error}</ErrorMessage>}

					<FieldGroup>
						<Label htmlFor="class-respondent">El teu nom (opcional)</Label>
						<Input
							id="class-respondent"
							type="text"
							placeholder="Ex: Maria"
							value={respondentLabel}
							onChange={(e) => setRespondentLabel(e.target.value)}
						/>
					</FieldGroup>

					<FieldGroup>
						<Label htmlFor="class-format">Unitat</Label>
						<Select
							id="class-format"
							value={unitat}
							onChange={(e) => setUnitat(e.target.value)}
						>
							<option value="">— Selecciona una unitat —</option>
							<option value="agrupament">Agrupament</option>
							<option value="varis">Varis / Desconegut</option>
							{unitats?.map((u) => (
								<option
									key={u._id}
									value={u.abreviacio.toLowerCase()}
								>
									{u.nom}
								</option>
							))}
						</Select>
					</FieldGroup>

					<FieldGroup>
						<Label htmlFor="class-year">Curs</Label>
						<Select
							id="class-year"
							value={schoolYear}
							onChange={(e) => setSchoolYear(e.target.value)}
						>
							{schoolYears.map((year) => (
								<option key={year} value={year}>
									{year}
								</option>
							))}
						</Select>
					</FieldGroup>

					<SliderContainer>
						<SliderHeader>
							<Label style={{ margin: 0 }}>
								Com de segur/a n'estàs?
							</Label>
							<SliderValue>{confidence}%</SliderValue>
						</SliderHeader>
						<Slider
							type="range"
							min={0}
							max={100}
							step={5}
							value={confidence}
							onChange={(e) =>
								setConfidence(parseInt(e.target.value, 10))
							}
						/>
						<SliderLabels>
							<span>No n'estic segur/a</span>
							<span>N'estic completament segur/a</span>
						</SliderLabels>
					</SliderContainer>
				</Body>
				<Actions>
					<CancelButton onClick={onClose}>Cancel·lar</CancelButton>
					<SubmitButton
						$submitted={!!submitted}
						onClick={handleSubmit}
						disabled={isSubmitting}
					>
						{isSubmitting
							? 'Enviant...'
							: submitted
							? 'Actualitzar classificació'
							: 'Classificar!'}
					</SubmitButton>
				</Actions>
			</Dialog>
		</Overlay>
	);
};

export default ClassificarDialog;
